import { Vector2, Vector3, Quaternion, Tools, Axis, BoundingBox } from "@babylonjs/core";
import _ from "lodash";
import { AngleRelation } from "../../../models/Types";

export function addVector2(v1: Vector2, v2: Vector2): Vector2 {
  const result = new Vector2(v1.x + v2.x, v1.y + v2.y);
  return result;
}

export function getDistance(v1: Vector3, v2: Vector3): number {
  const x = v2.x - v1.x;
  const x2 = Math.pow(x, 2);
  const y = v2.y - v1.y;
  const y2 = Math.pow(y, 2);
  const z = v2.z - v1.z;
  const z2 = Math.pow(z, 2);
  const result = Math.sqrt(x2 + y2 + z2);
  return result;
}

export const defaultScaleForWorldExtend = 0.2;

export interface Bounds {
  min: Vector3,
  max: Vector3,
}

export const zeroBounds: Bounds = {
  min: new Vector3(0,0,0),
  max: new Vector3(0,0,0),
};

export function getSizeForWorld(extend: Bounds, scale: number = defaultScaleForWorldExtend ): { width: number, depth: number } {
  const width = (extend.max.x - extend.min.x) * scale;
  const depth = (extend.max.z - extend.min.z) * scale;
  return { width, depth };
}

export function getBoundsForWorld(extend: Bounds, scale: number = defaultScaleForWorldExtend ): Bounds {
  const size = getSizeForWorld(extend, scale/2);
  return {
    min: new Vector3(0 - size.width, 0, 0 - size.depth),
    max: new Vector3(0 + size.width, 0, 0 + size.depth),
  };
}

export function calculateAngleInfoFromSourceToTarget(sourcePosition: Vector3, sourceRotation: Vector3, targetPosition: Vector3): AngleRelation {
  const directionToTarget = targetPosition.clone().subtract(sourcePosition).normalize();

  // Convert the source rotation vector to a quaternion
  const quaternion = Quaternion.FromEulerAngles(sourceRotation.x, sourceRotation.y, sourceRotation.z);

  // Transform the forward direction by the rotation quaternion
  const forwardDirection = Vector3.Forward().rotateByQuaternionToRef(quaternion, Vector3.Zero());

  const dotProduct = _.clamp(Vector3.Dot(forwardDirection, directionToTarget), -1, 1);
  const crossProduct = Vector3.Cross(forwardDirection, directionToTarget);

  const shortestAngleRadians = Math.acos(dotProduct);
  let clockwiseAngleRadians = shortestAngleRadians;

  const isPanningLeft: boolean = Vector3.Dot(crossProduct, Vector3.Up()) < 0;
  if (isPanningLeft) {
      clockwiseAngleRadians = Math.PI * 2 - clockwiseAngleRadians;
  }

  return { shortestAngleRadians, clockwiseAngleRadians };
}

export function calculateLinearGradient(x1: number, x2: number, y1: number, y2: number) {
  return (y2-y1)/(x2-x1);
}

export function interpolate(x: number, xMin: number, xMax: number, yAtMin: number, yAtMax: number) {
  x = _.clamp(x, xMin, xMax);
  if (xMin === xMax) {
    console.error('interpolate xMin and xMax equal, defaulting to yMin');
    return yAtMin;
  }
  const interpolationFactor = (x - xMin) / (xMax - xMin);
  const interpolatedValue = yAtMin + interpolationFactor * (yAtMax - yAtMin);
  return interpolatedValue;
}

export function smoothInterpolate(x: number, xMin: number, xMax: number, yAtMin: number, yAtMax: number, xOffset: number, yInitial: number) {
  x = _.clamp(x, xMin, xMax);
  xOffset = _.clamp(xOffset, xMin, xMax);
  if (x < xOffset) {
    return interpolate(x, xMin, xOffset, yInitial, yAtMin);
  } else {
    return interpolate(x, xOffset, xMax, yAtMin, yAtMax);
  }
}

export function isPointIntersectingBox(p: Vector3, box: BoundingBox) {
  const withinMin = p.x >= box.minimumWorld.x && p.y >= box.minimumWorld.y && p.z >= box.minimumWorld.z;
  const withinMax = p.x <= box.maximumWorld.x && p.y <= box.maximumWorld.y && p.z <= box.maximumWorld.z;
  return withinMin && withinMax;
}
